import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { useListingModeStore } from '@services/livability/store'
import type {
  ApartmentFilterParams,
  ListingType
} from '@services/livability/types'

import {
  hasPersistedApartmentState,
  isBareApartmentUrl,
  loadPersistedApartmentView,
  savePersistedApartmentView
} from '../utils/apartmentFilterPersist'
import {
  type ApartmentFilters,
  buildApartmentFilterSearchParams,
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  filtersToApiParams,
  hasActiveApartmentFilters,
  parseApartmentViewParams
} from '../utils/apartmentFilterUrl'
import { isApartmentListPath, isApartmentViewPath } from '../utils/apartmentNav'

function preserveMapOnlyKeys(
  next: URLSearchParams,
  current: URLSearchParams
): URLSearchParams {
  for (const key of ['ids', 'from', 'lat', 'lng', 'zoom'] as const) {
    const value = current.get(key)
    if (value) next.set(key, value)
  }
  return next
}

export function useApartmentFilters() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()
  const storeListingMode = useListingModeStore((s) => s.listingMode)
  const setListingMode = useListingModeStore((s) => s.setListingMode)

  const isMapView = pathname.includes('/apartments-map')
  const isListView = isApartmentListPath(pathname)
  const isApartmentView = isApartmentViewPath(pathname)
  const restoredBareUrlRef = useRef<string | null>(null)

  const state = useMemo(
    () => parseApartmentViewParams(searchParams),
    [searchParams]
  )

  const listingType = searchParams.has('listing_type')
    ? state.listingType
    : storeListingMode

  useEffect(() => {
    if (
      searchParams.has('listing_type') &&
      state.listingType !== storeListingMode
    ) {
      setListingMode(state.listingType)
    }
  }, [searchParams, state.listingType, storeListingMode, setListingMode])

  const replaceSearchParams = useCallback(
    (next: URLSearchParams) => {
      setSearchParams(next, { replace: true })
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (!isApartmentView) return
    if (!isBareApartmentUrl(searchParams)) {
      restoredBareUrlRef.current = null
      return
    }

    const visitKey = pathname
    if (restoredBareUrlRef.current === visitKey) return

    const saved = loadPersistedApartmentView()
    if (!hasPersistedApartmentState(saved)) return

    restoredBareUrlRef.current = visitKey
    const params = preserveMapOnlyKeys(
      buildApartmentFilterSearchParams(saved.filters, {
        q: saved.q,
        page: saved.page,
        limit: saved.limit,
        listingType: saved.listingType,
        includeListParams: isListView
      }),
      searchParams
    )
    replaceSearchParams(params)
  }, [isApartmentView, isListView, pathname, replaceSearchParams, searchParams])

  useEffect(() => {
    if (!isApartmentView) return
    if (isBareApartmentUrl(searchParams)) return

    savePersistedApartmentView({
      filters: state.filters,
      q: state.q,
      page: state.page,
      limit: state.limit,
      listingType
    })
  }, [
    isApartmentView,
    listingType,
    searchParams,
    state.filters,
    state.limit,
    state.listingType,
    state.page,
    state.q
  ])

  const setFilters = useCallback(
    (filters: ApartmentFilters, options?: { resetPage?: boolean }) => {
      const resetPage = options?.resetPage ?? true
      const page = resetPage ? DEFAULT_PAGE : state.page

      // Persist before URL update — removing the last filter yields a "bare"
      // URL and the restore effect would otherwise re-apply stale session state.
      savePersistedApartmentView({
        filters,
        q: state.q,
        page,
        limit: state.limit,
        listingType
      })

      const params = preserveMapOnlyKeys(
        buildApartmentFilterSearchParams(filters, {
          q: state.q,
          page,
          limit: state.limit,
          listingType,
          includeListParams: !isMapView
        }),
        searchParams
      )
      replaceSearchParams(params)
    },
    [
      isMapView,
      listingType,
      replaceSearchParams,
      searchParams,
      state.limit,
      state.page,
      state.q
    ]
  )

  const setFilter = useCallback(
    <K extends keyof ApartmentFilters>(
      key: K,
      value: ApartmentFilters[K],
      options?: { resetPage?: boolean }
    ) => {
      const nextFilters = { ...state.filters }
      if (value === undefined) {
        delete nextFilters[key]
      } else {
        nextFilters[key] = value
      }
      setFilters(nextFilters, options)
    },
    [setFilters, state.filters]
  )

  const clearFilters = useCallback(() => {
    savePersistedApartmentView({
      filters: {},
      q: isMapView ? '' : state.q,
      page: DEFAULT_PAGE,
      limit: state.limit,
      listingType
    })

    const params = new URLSearchParams()
    params.set('listing_type', listingType)
    if (!isMapView) {
      if (state.q) params.set('q', state.q)
      if (state.limit !== DEFAULT_LIMIT) {
        params.set('limit', String(state.limit))
      }
    }
    preserveMapOnlyKeys(params, searchParams)
    replaceSearchParams(params)
  }, [
    isMapView,
    listingType,
    replaceSearchParams,
    searchParams,
    state.limit,
    state.q
  ])

  const setQ = useCallback(
    (q: string) => {
      savePersistedApartmentView({
        filters: state.filters,
        q,
        page: DEFAULT_PAGE,
        limit: state.limit,
        listingType
      })

      const params = preserveMapOnlyKeys(
        buildApartmentFilterSearchParams(state.filters, {
          q,
          page: DEFAULT_PAGE,
          limit: state.limit,
          listingType,
          includeListParams: !isMapView
        }),
        searchParams
      )
      replaceSearchParams(params)
    },
    [
      isMapView,
      listingType,
      replaceSearchParams,
      searchParams,
      state.filters,
      state.limit
    ]
  )

  const setPage = useCallback(
    (page: number) => {
      savePersistedApartmentView({
        filters: state.filters,
        q: state.q,
        page,
        limit: state.limit,
        listingType
      })

      const params = preserveMapOnlyKeys(
        buildApartmentFilterSearchParams(state.filters, {
          q: state.q,
          page,
          limit: state.limit,
          listingType,
          includeListParams: !isMapView
        }),
        searchParams
      )
      replaceSearchParams(params)
    },
    [
      isMapView,
      listingType,
      replaceSearchParams,
      searchParams,
      state.filters,
      state.limit,
      state.q
    ]
  )

  const setListingTypeInUrl = useCallback(
    (mode: ListingType) => {
      setListingMode(mode)

      savePersistedApartmentView({
        filters: state.filters,
        q: state.q,
        page: DEFAULT_PAGE,
        limit: state.limit,
        listingType: mode
      })

      const params = preserveMapOnlyKeys(
        buildApartmentFilterSearchParams(state.filters, {
          q: state.q,
          page: DEFAULT_PAGE,
          limit: state.limit,
          listingType: mode,
          includeListParams: !isMapView
        }),
        searchParams
      )
      replaceSearchParams(params)
    },
    [
      isMapView,
      replaceSearchParams,
      searchParams,
      setListingMode,
      state.filters,
      state.limit,
      state.q
    ]
  )

  const apiParams: ApartmentFilterParams = useMemo(
    () =>
      filtersToApiParams(state.filters, {
        q: state.q,
        page: state.page,
        limit: state.limit
      }),
    [state.filters, state.limit, state.page, state.q]
  )

  const indexCategory = state.filters.indexCategory ?? 'general'

  return {
    filters: state.filters,
    q: state.q,
    page: state.page,
    limit: state.limit,
    listingType,
    indexCategory,
    apiParams,
    hasActiveFilters: hasActiveApartmentFilters(state.filters),
    setFilter,
    setFilters,
    clearFilters,
    setQ,
    setPage,
    setListingTypeInUrl
  }
}
